/* eslint-disable @typescript-eslint/no-explicit-any */
import Logo from './Logo';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { BsGear } from 'react-icons/bs';
import appAtom from '../atoms/app.atom';
import authAtom from '../atoms/auth.atom';
import { AiOutlineSwap } from 'react-icons/ai';
import { useLogout } from '../hooks/auth.hook';
import { handleCapitalize, isEqual } from '../contexts/Utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { TbLayoutSidebarRightInactive } from 'react-icons/tb';
import { HiOutlineTicket, HiOutlineUserGroup } from 'react-icons/hi';
import { LuLayoutDashboard, LuLogOut, LuWallet } from 'react-icons/lu';
import { Avatar, Button, Divider, Drawer, Dropdown, Menu } from 'antd';
import { PiHeadset, PiMoneyWavyLight, PiPlugCharging } from 'react-icons/pi';

type Props = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  isOpenMobile: boolean;
  onOpenMobile: () => void;
  onCloseMobile: () => void;
};
function SideBar({ isOpen, onOpen, onClose, isOpenMobile, onCloseMobile }: Props) {
  const isMobile = ((window.innerWidth <= 500) && isOpenMobile);
  const handleOpen = () => (isOpen ? onClose() : onOpen());
  const { estate } = useRecoilValue(appAtom);
  const { unit } = useRecoilValue(authAtom);
  const { pathname } = useLocation();

  const modules = (unit?.modules || []);
  const handleCheck = (name: string) => modules?.some((d: any) => isEqual(d?.slug, name) && d?.has_access);

  const navigate = useNavigate();
  const items = [
    {
      key: '/dashboard',
      label: 'Dashboard',
      icon: <LuLayoutDashboard className="!text-lg" />,
    },
    {
      key: '/billings',
      label: 'Billings',
      hide: !handleCheck("billing"),
      icon: <PiMoneyWavyLight className="!text-lg" />,
    },
    {
      key: '/vending',
      label: 'Vending',
      hide: !handleCheck("vending"),
      icon: <PiPlugCharging className="!text-lg" />,
    },
    {
      key: '/visitors',
      label: 'Visitors',
      hide: !handleCheck("visitation"),
      icon: <HiOutlineUserGroup className="!text-lg" />,
    },
    {
      key: '/wallet',
      label: 'Wallet',
      icon: <LuWallet className="!text-lg" />,
    },
    {
      key: '/complaints',
      label: 'Tickets & Complains',
      hide: !handleCheck("ticket"),
      icon: <HiOutlineTicket className="!text-lg" />,
    },
    {
      key: '/transactions',
      label: 'Transactions',
      icon: <AiOutlineSwap className="!text-lg" />,
    },
  ].filter(({hide}) => !hide);

  const extras = [
    {
      key: '/settings',
      label: 'Settings',
      icon: <BsGear className="!text-lg" />,
    },
    {
      key: '/page/contact',
      label: 'Support',
      icon: <PiHeadset className="!text-lg" />,
    },
  ];

  const { user } = useRecoilValue(authAtom);
  const { mutate: logoutAction, isLoading: logoutLoad } = useLogout();

  const handleMenu = ({ key }: { key: string }) => navigate(key);
  const handleLogout = () => navigate('/auth/logout');

  const MenuContent = () => (
    <div className="w-full h-full flex flex-col justify-between items-center py-5 px-2 relative">
      <div className="w-full flex flex-col justify-center items-center gap-3">
        <div className="hidden md:flex justify-between items-center gap-5">
          <Logo className="!w-[50px]" path="/" />
          <div hidden={isOpen}>
            <p className="text-base font-medium text-dark capitalize">{estate || "Estate Plus"}</p>
            <p className="text-xs font-normal text-gray">Resident User</p>
          </div>
          <Button
            size="small"
            onClick={handleOpen}
            icon={<TbLayoutSidebarRightInactive className="!text-lg rotate-180" />}
            className={`!rounded-md !z-30 hidden md:block ${isOpen && 'absolute top-10 -right-5'}`}
          />
        </div>
        <div className="w-full max-h-[65vh] overflow-y-auto scrollbar-none">
          <Menu
            style={{
              border: 0,
              color: '#525866',
              fontSize: '14px',
              fontWeight: '400',
              background: 'transparent',
            }}
            defaultSelectedKeys={[pathname]}
            defaultOpenKeys={[pathname]}
            inlineCollapsed={isOpen}
            onClick={handleMenu}
            key="main-menu"
            mode="inline"
            items={items}
          />
        </div>
      </div>

      <div className="w-full flex flex-col justify-center items-center">
        <Menu
          style={{
            border: 0,
            color: '#525866',
            fontSize: '14px',
            fontWeight: '400',
            background: 'transparent',
          }}
          defaultSelectedKeys={[pathname]}
          defaultOpenKeys={[pathname]}
          inlineCollapsed={isOpen}
          onClick={handleMenu}
          key="extra-menu"
          items={extras}
          mode="inline"
        />
        <Divider className="!my-2" />
        <div
          className={'w-full flex items-center px-3 '.concat(
            isOpen ? 'justify-center' : 'justify-between'
          )}
        >
          <div className="flex items-center gap-3">
            <Avatar alt="user" size="large" src={user?.profile_img}>
              {handleCapitalize(user?.name?.[0])}
            </Avatar>
            <div hidden={isOpen} className="w-[120px]">
              <p title={user?.name} className="text-dark text-sm truncate">
                {user?.name || 'FM User'}
              </p>
              <p title={user?.email} className="text-gray text-xs truncate">
                {user?.email || 'fm-user@estateplus.com.ng'}
              </p>
            </div>
          </div>
          <Dropdown trigger={["click"]} menu={{items: [
            {key: "normal", label: "Logout", onClick: logoutAction as any},
            {key: "clear", label: "Clear Cache & Logout", onClick: handleLogout},
          ]}}>
            <Button
              type="text"
              title="Logout"
              hidden={isOpen}
              loading={logoutLoad}
              className="text-secondary !p-0 !m-0"
              icon={<LuLogOut className="!text-lg !font-medium" />}
            />
          </Dropdown>
        </div>
      </div>
    </div>
  );
  return (
    <div className="h-full shadow bg-white border-r border-outline !w-auto">
      <div className="hidden md:block w-full h-full ">
        <MenuContent />
      </div>
      <Drawer
        width={250}
        footer={false}
        open={isMobile}
        closeIcon={false}
        onClose={onCloseMobile}
        classNames={{body: "!p-0"}}
      >
        <MenuContent />
      </Drawer>
    </div>
  );
}

export default SideBar;
